import { type ReactNode, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { fmtFamilies } from "@/components/admin/adminFormat";
import { ChevronRightIcon } from "@/design/icons";
import { Muted, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";

export type FamilyWatchBreakdownRow = {
  key: string;
  email: string;
  summary: string;
  visual?: ReactNode;
  expandedContent?: ReactNode;
};

/** Shared household list used beneath both daily and per-dance watch graphs. */
export function FamilyWatchBreakdown({
  allowOverflow = false,
  compact,
  rows
}: {
  /** Retention previews can intentionally escape the rounded list surface. */
  allowOverflow?: boolean;
  compact: boolean;
  rows: FamilyWatchBreakdownRow[];
}) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  if (rows.length === 0) return null;

  return (
    <View style={styles.breakdown}>
      <View style={styles.header}>
        <Text variant="overline" color={palette.muted}>Families who watched</Text>
        <Muted variant="small">{fmtFamilies(rows.length)}</Muted>
      </View>
      <View style={[styles.list, allowOverflow && styles.listOverflowVisible]}>
        {rows.map((row, index) => {
          const expandable = row.expandedContent != null;
          const expanded = expandable && expandedKey === row.key;
          const rowContent = (
            <>
              <View style={[
                styles.identity,
                expandable && styles.identityExpandable,
                compact && styles.identityCompact,
                expandable && compact && styles.identityExpandableCompact
              ]}>
                <Text variant="smallStrong" numberOfLines={1} style={styles.email}>
                  {row.email}
                </Text>
                <Muted variant="small">{row.summary}</Muted>
              </View>
              {row.visual}
              {expandable ? (
                <View style={[styles.chevron, expanded && styles.chevronExpanded]}>
                  <ChevronRightIcon size={16} color={palette.muted} />
                </View>
              ) : null}
            </>
          );
          return (
            <View key={row.key}>
              {expandable ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`${row.email}. ${expanded ? "Hide" : "Show"} dances watched.`}
                  accessibilityState={{expanded}}
                  onPress={() => setExpandedKey(expanded ? null : row.key)}
                  style={({hovered}) => [
                    styles.row,
                    compact && styles.rowCompact,
                    compact && styles.rowCompactExpandable,
                    index > 0 && styles.divider,
                    hovered && styles.rowHovered
                  ]}
                >
                  {rowContent}
                </Pressable>
              ) : (
                <View style={[
                  styles.row,
                  compact && styles.rowCompact,
                  index > 0 && styles.divider
                ]}>
                  {rowContent}
                </View>
              )}
              {expanded ? (
                <View style={styles.expandedContent}>{row.expandedContent}</View>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  breakdown: {
    gap: space.md,
    marginTop: space.lg
  },
  header: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  list: {
    borderColor: palette.hairline,
    borderRadius: radius.sm,
    borderWidth: 1,
    overflow: "hidden"
  },
  listOverflowVisible: {
    overflow: "visible"
  },
  row: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    minHeight: 54,
    paddingHorizontal: space.md,
    paddingVertical: space.sm
  },
  rowCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: space.sm
  },
  rowCompactExpandable: {
    alignItems: "center",
    flexDirection: "row"
  },
  divider: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1
  },
  identity: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.md,
    justifyContent: "space-between",
    minWidth: 0,
    width: 280
  },
  identityCompact: {
    width: "100%"
  },
  identityExpandable: {
    flex: 1,
    width: "auto"
  },
  identityExpandableCompact: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 2,
    width: "auto"
  },
  email: {
    flex: 1
  },
  rowHovered: {
    backgroundColor: palette.coolWashHover
  },
  chevron: {
    alignItems: "center",
    flexShrink: 0,
    justifyContent: "center",
    width: 18,
    transform: [{rotate: "0deg"}]
  },
  chevronExpanded: {
    transform: [{rotate: "90deg"}]
  },
  expandedContent: {
    backgroundColor: palette.coolWashFaint,
    borderTopColor: palette.hairline,
    borderTopWidth: 1,
    padding: space.md
  }
});
